import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Scroll ergonomics for long pages (business-owner feedback: "the hamburger button stays
 * at the top of the page — opening the menu after scrolling down means scrolling all the
 * way back up"). Two complementary behaviors, both requested:
 *
 * 1. `useAutoHideOnScroll` — the mobile topbar becomes sticky and slides away while
 *    scrolling down, then reappears the moment the user scrolls up a little, so the
 *    hamburger is always one small upward flick away. Class toggling happens imperatively
 *    on the ref'd element (never React state), so scrolling re-renders nothing.
 * 2. `BackToTopButton` — a floating button that appears once the page is scrolled well
 *    past the first screen and smooth-scrolls back to the top.
 *
 * Both watch **window** scroll: `NavShell` is `min-height: 100vh` with no inner scroll
 * container — the document itself is what scrolls in this app.
 */

const HIDE_AFTER_PX = 64; // never hide within the first screen-top — jitter guard
const DIRECTION_THRESHOLD_PX = 6; // ignore sub-threshold scroll noise (rubber-banding etc.)

export function useAutoHideOnScroll(ref: RefObject<HTMLElement | null>, hiddenClass: string): void {
  useEffect(() => {
    let lastY = window.scrollY;
    function onScroll() {
      const el = ref.current;
      if (!el) return;
      const y = window.scrollY;
      const delta = y - lastY;
      if (y <= HIDE_AFTER_PX) {
        el.classList.remove(hiddenClass);
      } else if (delta > DIRECTION_THRESHOLD_PX) {
        el.classList.add(hiddenClass);
      } else if (delta < -DIRECTION_THRESHOLD_PX) {
        el.classList.remove(hiddenClass);
      }
      lastY = y;
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ref, hiddenClass]);
}

const SHOW_BACK_TO_TOP_AFTER_PX = 500;

export function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);

  useEffect(() => {
    function onScroll() {
      const next = window.scrollY > SHOW_BACK_TO_TOP_AFTER_PX;
      // Only touch state on the threshold crossing, not every scroll frame.
      if (next !== visibleRef.current) {
        visibleRef.current = next;
        setVisible(next);
      }
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      className={["sl-back-to-top", visible ? "sl-back-to-top--visible" : null].filter(Boolean).join(" ")}
      aria-label="Back to top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      ↑ Top
    </button>
  );
}
